import type { Setting } from "model/Workgroup";

interface TimeReportsDisplaySettings {
  deviationToleranceMinutes: number;
  includeFlexTimeDeviations: boolean;
}

const COMPANY_TIME_REPORT_DEVIATION_THRESHOLD =
  "CompanyTimeReportDeviationThreshold";
const COMPANY_TIME_REPORT_INCLUDE_FLEX_TIMES =
  "CompanyTimeReportIncludeFlexTimes";
const DEFAULT_DEVIATION_TOLERANCE_MINUTES = 10;

const resolveCompanySettingValue = (
  companySettings: Setting[] | undefined,
  key: string,
): string | undefined => {
  return companySettings?.find((setting) => setting.key === key)?.value;
};

/** Resolves deviation threshold minutes from company settings with a safe default. */
export const resolveDeviationToleranceMinutes = (
  companySettings: Setting[] | undefined,
): number => {
  const rawValue = resolveCompanySettingValue(
    companySettings,
    COMPANY_TIME_REPORT_DEVIATION_THRESHOLD,
  );
  const parsed = Number(rawValue);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return DEFAULT_DEVIATION_TOLERANCE_MINUTES;
  }
  return parsed;
};

/** Resolves whether flex-time deviations should be included in deviation display logic. */
export const resolveIncludeFlexTimeDeviations = (
  companySettings: Setting[] | undefined,
): boolean => {
  const rawValue = resolveCompanySettingValue(
    companySettings,
    COMPANY_TIME_REPORT_INCLUDE_FLEX_TIMES,
  );
  return rawValue?.trim().toLowerCase() === "true";
};

/** Resolves time-reports display settings from company system settings with safe defaults. */
export const resolveTimeReportsDisplaySettings = (
  companySettings: Setting[] | undefined,
): TimeReportsDisplaySettings => {
  return {
    deviationToleranceMinutes:
      resolveDeviationToleranceMinutes(companySettings),
    includeFlexTimeDeviations:
      resolveIncludeFlexTimeDeviations(companySettings),
  };
};
